import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  FileInput,
  Flex,
  Loader,
  NumberInput,
  Paper,
  Progress,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import type {
  SpcApiInspectionsInspectionIdContextPutRequest,
  SpcApiInspectionsInspectionIdSubmitPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCheck,
  IconCircleDashed,
  IconClock,
  IconDeviceFloppy,
  IconFlag3,
} from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import {
  InspectionCriteria,
  InspectionUnit,
  UnitStatus,
} from "@/components/organism/form/inventoryTransaction/types";
import { useInspection } from "@/components/organism/form/inventoryTransaction/InspectionContext";
import { InspectionMutations } from "@/api/inspect/useInspectQuery";
import { customNotification } from "@/utils/notificationShow";

interface InspectionExecutionProps {
  onCancel?: () => void;
  onComplete?: () => void;
  recordForm?: UseFormReturnType<SpcApiInspectionsInspectionIdSubmitPostRequest>;
  onSubmitInspection?: (summary: { sampleCount: number }) => void;
  showActionBar?: boolean;
}

const statusMeta: Record<UnitStatus, { label: string; color: string }> = {
  NOT_STARTED: { label: "대기", color: "gray" },
  IN_PROGRESS: { label: "진행중", color: "blue" },
  DONE: { label: "완료", color: "green" },
  DEFECT: { label: "불량", color: "red" },
};

const criteriaTypeMeta: Record<
  InspectionCriteria["type"],
  { label: string; color: string }
> = {
  boolean: { label: "합/불", color: "green" },
  number: { label: "수치", color: "blue" },
  select: { label: "선택", color: "grape" },
  photo: { label: "사진", color: "cyan" },
  description: { label: "기록", color: "gray" },
};

const getCriteriaSpec = (criteria: InspectionCriteria) => {
  if (
    criteria.type === "number" &&
    (criteria.min !== undefined || criteria.max !== undefined)
  ) {
    return `합격 범위 : ${criteria.min ?? "-"} ~ ${criteria.max ?? "-"}`;
  }

  return criteria.spec ?? "-";
};

type DescriptionCellState = { text: string; isPassed: boolean };

const getDescriptionCellState = (value: unknown): DescriptionCellState => {
  if (value && typeof value === "object") {
    const recordValue = value as {
      text?: unknown;
      value?: unknown;
      isPassed?: unknown;
    };
    const textValue =
      typeof recordValue.text === "string"
        ? recordValue.text
        : typeof recordValue.value === "string"
          ? recordValue.value
          : "";
    const passValue =
      typeof recordValue.isPassed === "boolean" ? recordValue.isPassed : false;
    return { text: textValue, isPassed: passValue };
  }

  if (typeof value === "string") {
    return { text: value, isPassed: false };
  }

  return { text: "", isPassed: false };
};

const isValueProvided = (criteria: InspectionCriteria, value: unknown) => {
  if (value === undefined || value === null) {
    return false;
  }
  if (criteria.type === "boolean") {
    return typeof value === "boolean";
  }
  if (criteria.type === "description") {
    const descriptionValue = getDescriptionCellState(value);
    return descriptionValue.text.trim().length > 0;
  }
  if (criteria.type === "number") {
    return value !== "" && !Number.isNaN(Number(value));
  }
  if (criteria.type === "select") {
    return String(value).length > 0;
  }
  if (criteria.type === "photo") {
    return Boolean(value);
  }
  return true;
};

export function InspectionExecution({
  onCancel,
  onComplete,
  recordForm,
  onSubmitInspection,
  showActionBar = true,
}: InspectionExecutionProps) {
  const {
    currentPlan,
    setCurrentPlan,
    inspectionCriteria,
    inspectionUnits,
    setInspectionUnits,
    currentUnitIndex,
    setCurrentUnitIndex,
  } = useInspection();

  const [autoSaveStatus, setAutoSaveStatus] = useState<
    "idle" | "saving" | "saved"
  >("idle");
  const [lastSavedInfo, setLastSavedInfo] = useState<{
    unitLabel: string;
    timestamp: number;
  } | null>(null);
  const saveTimerRef = useRef<number | null>(null);
  const [unitLabelDrafts, setUnitLabelDrafts] = useState<
    Record<string, string>
  >({});
  const [renamingUnitId, setRenamingUnitId] = useState<string | null>(null);
  const inspectionId = recordForm?.values.inspectionId ?? 0;
  const { mutateAsync: updateInspectionContext } = useMutation({
    mutationFn: (params: SpcApiInspectionsInspectionIdContextPutRequest) =>
      InspectionMutations.contextUpdate(params),
  });

  const currentUnit = inspectionUnits[currentUnitIndex] ?? inspectionUnits[0];

  useEffect(() => {
    setInspectionUnits((prevUnits) => {
      let didUpdate = false;
      const nextUnits = prevUnits.map((unit) => {
        if (unit.originalLabel) {
          return unit;
        }
        didUpdate = true;
        return { ...unit, originalLabel: unit.label };
      });
      return didUpdate ? nextUnits : prevUnits;
    });
  }, [setInspectionUnits]);

  const isUnitComplete = (unitData: InspectionUnit["data"] = {}) =>
    inspectionCriteria.every((criteria) => {
      if (!criteria.required) return true;
      return isValueProvided(criteria, unitData[criteria.id]);
    });

  const completedCount = useMemo(
    () => inspectionUnits.filter((unit) => isUnitComplete(unit.data)).length,
    [inspectionUnits],
  );
  const progressValue =
    inspectionUnits.length > 0
      ? Math.round((completedCount / inspectionUnits.length) * 100)
      : 0;
  const readyToFinish =
    inspectionUnits.length > 0 &&
    inspectionUnits.every((unit) => isUnitComplete(unit.data));
  const checkColumnStates = useMemo(() => {
    const states: Record<string, { checked: boolean; indeterminate: boolean }> =
      {};
    inspectionCriteria.forEach((criteria) => {
      if (criteria.type !== "boolean") return;
      const totalUnits = inspectionUnits.length;
      let passCount = 0;
      let failCount = 0;
      inspectionUnits.forEach((unit) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const cellValue = unit.data?.[criteria.id];
        if (cellValue === true) {
          passCount += 1;
        } else if (cellValue === false) {
          failCount += 1;
        }
      });
      const pendingCount = totalUnits - passCount - failCount;
      states[criteria.id] = {
        checked: totalUnits > 0 && passCount === totalUnits,
        indeterminate:
          (passCount > 0 && passCount < totalUnits) ||
          (failCount > 0 && failCount < totalUnits) ||
          pendingCount > 0,
      };
    });
    return states;
  }, [inspectionCriteria, inspectionUnits]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  const handleUnitSelect = (index: number) => {
    setCurrentUnitIndex(index);
  };

  const recordFieldBase = "inspectionsInspectionIdSubmitPostRequest.record";

  const normalizeRecordValue = (
    criteriaType: InspectionCriteria["type"],
    rawValue: unknown,
  ) => {
    if (rawValue === undefined) {
      return null;
    }
    if (rawValue === null) {
      return null;
    }
    switch (criteriaType) {
      case "number": {
        const numericValue =
          typeof rawValue === "number" ? rawValue : Number(rawValue);
        return Number.isFinite(numericValue) ? numericValue : null;
      }
      case "boolean":
        return typeof rawValue === "boolean" ? rawValue : null;
      case "select":
        return typeof rawValue === "string" ? rawValue : null;
      case "description":
        return getDescriptionCellState(rawValue).text;
      case "photo":
        if (Array.isArray(rawValue)) {
          // Store file names since API expects primitive values.
          return rawValue
            .map((file) =>
              "name" in file ? (file as File).name : String(file),
            )
            .join(", ");
        }
        return typeof rawValue === "string" ? rawValue : null;
      default:
        if (
          typeof rawValue === "string" ||
          typeof rawValue === "number" ||
          typeof rawValue === "boolean"
        ) {
          return rawValue;
        }
        return null;
    }
  };

  const syncRecordFormValue = (
    criteriaId: string,
    unitIndex: number,
    rawValue: unknown,
  ) => {
    if (!recordForm) return;
    const criteriaIndex = inspectionCriteria.findIndex(
      (criteria) => criteria.id === criteriaId,
    );
    if (criteriaIndex === -1) return;
    const criteria = inspectionCriteria[criteriaIndex];
    const normalizedValue = normalizeRecordValue(criteria.type, rawValue);
    const record =
      recordForm.values.inspectionsInspectionIdSubmitPostRequest.record ?? [];
    const existingCell = record?.[criteriaIndex]?.[unitIndex];
    const descriptionMeta =
      criteria.type === "description"
        ? getDescriptionCellState(rawValue)
        : null;
    const nextCell = {
      ...existingCell,
      value: normalizedValue,
      ...(descriptionMeta ? { isPassed: descriptionMeta.isPassed } : {}),
    };
    const shouldSkipUpdate =
      existingCell &&
      existingCell.value === normalizedValue &&
      (criteria.type !== "description" ||
        (descriptionMeta &&
          existingCell.isPassed === descriptionMeta.isPassed));
    if (shouldSkipUpdate) {
      return;
    }
    recordForm.setFieldValue(
      `${recordFieldBase}.${criteriaIndex}.${unitIndex}`,
      nextCell,
    );
  };

  const syncRecordFormColumn = (criteriaId: string, rawValue: unknown) => {
    inspectionUnits.forEach((_unit, unitIndex) => {
      syncRecordFormValue(criteriaId, unitIndex, rawValue);
    });
  };

  const handleSampleLabelInputChange = (unitId: string, value: string) => {
    setUnitLabelDrafts((prev) => ({
      ...prev,
      [unitId]: value,
    }));
  };

  const resetUnitLabelDraft = (unitId: string) => {
    setUnitLabelDrafts((prev) => {
      if (!(unitId in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[unitId];
      return next;
    });
  };

  const triggerAutoSaveFeedback = (unitLabel: string) => {
    setAutoSaveStatus("saving");
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = window.setTimeout(() => {
      setAutoSaveStatus("saved");
      setLastSavedInfo({ unitLabel, timestamp: Date.now() });
    }, 400);
  };

  const handleCellChange = (
    unitId: string,
    criteriaId: string,
    value: unknown,
  ) => {
    let touchedLabel = "";
    const unitIndex = inspectionUnits.findIndex((unit) => unit.id === unitId);
    setInspectionUnits((prev: InspectionUnit[]) =>
      prev.map((unit) => {
        if (unit.id !== unitId) {
          return unit;
        }
        touchedLabel = unit.label;

        const nextData = {
          ...unit.data,
          [criteriaId]: value,
        };

        const unitComplete = isUnitComplete(nextData);

        const nextStatus =
          unitComplete || unit.status === "DONE"
            ? "DONE"
            : unit.status === "NOT_STARTED"
              ? "IN_PROGRESS"
              : unit.status;

        return {
          ...unit,
          status: nextStatus,
          data: nextData,
        };
      }),
    );
    triggerAutoSaveFeedback(touchedLabel || unitId);
    if (unitIndex >= 0) {
      syncRecordFormValue(criteriaId, unitIndex, value);
    }
  };

  const handleCheckColumnToggle = (
    criteriaId: string,
    value: boolean | null,
  ) => {
    const criteriaName = inspectionCriteria.find(
      (criteria) => criteria.id === criteriaId,
    )?.name;
    setInspectionUnits((prev: InspectionUnit[]) =>
      prev.map((unit) => {
        const nextData = {
          ...unit.data,
          [criteriaId]: value,
        };
        const unitComplete = isUnitComplete(nextData);
        const nextStatus =
          unitComplete || unit.status === "DONE"
            ? "DONE"
            : unit.status === "NOT_STARTED"
              ? "IN_PROGRESS"
              : unit.status;
        return {
          ...unit,
          status: nextStatus,
          data: nextData,
        };
      }),
    );
    const targetLabel =
      value === null
        ? `${criteriaName ?? criteriaId} 초기화`
        : `${criteriaName ?? criteriaId} 전체 ${value ? "합격" : "불합격"}`;
    triggerAutoSaveFeedback(targetLabel);
    syncRecordFormColumn(criteriaId, value);
  };

  const handleSampleLabelSubmit = async (
    unit: InspectionUnit,
    rowIndex: number,
  ) => {
    const draftValue = unitLabelDrafts[unit.id];
    const nextLabel = (draftValue ?? unit.label).trim();
    if (nextLabel.length === 0 || nextLabel === unit.label) {
      resetUnitLabelDraft(unit.id);
      return;
    }
    if (!inspectionId) {
      customNotification.error({
        message: "검사 정보가 없어 샘플명을 변경할 수 없습니다.",
      });

      resetUnitLabelDraft(unit.id);
      return;
    }
    setRenamingUnitId(unit.id);
    try {
      const payloadUnits = inspectionUnits.map((currentUnit, index) => ({
        unitIndex: currentUnit.unitIndex ?? index + 1,
        unitCode: currentUnit.id === unit.id ? nextLabel : currentUnit.label,
      }));
      await updateInspectionContext({
        inspectionId,
        inspectionsInspectionIdContextPutRequest: {
          context: {
            units: payloadUnits,
          },
        },
      });
      setInspectionUnits((prevUnits) =>
        prevUnits.map((currentUnit) => {
          if (currentUnit.id !== unit.id) {
            return currentUnit;
          }
          return {
            ...currentUnit,
            label: nextLabel,
            originalLabel: currentUnit.originalLabel ?? currentUnit.label,
          };
        }),
      );
      setCurrentPlan((prevPlan) => {
        if (!prevPlan?.sampleList || prevPlan.sampleList.length === 0) {
          return prevPlan;
        }
        const nextSampleList = prevPlan.sampleList.map((sample, index) =>
          index === rowIndex ? nextLabel : sample,
        );
        return { ...prevPlan, sampleList: nextSampleList };
      });
      resetUnitLabelDraft(unit.id);
    } catch (error) {
      console.error(error);

      customNotification.error({
        message: "샘플 이름을 변경하지 못했습니다.",
      });
      resetUnitLabelDraft(unit.id);
    } finally {
      setRenamingUnitId(null);
    }
  };

  const handleSampleInputKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    unit: InspectionUnit,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.currentTarget.blur();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      resetUnitLabelDraft(unit.id);
      event.currentTarget.blur();
    }
  };

  const handleCompleteInspection = () => {
    if (!readyToFinish || !currentPlan) return;
    onSubmitInspection?.({ sampleCount: inspectionUnits.length });
    setCurrentPlan({ ...currentPlan, status: "SUBMITTED" });
    onComplete?.();
  };

  if (!currentPlan) {
    return (
      <Stack spacing="lg" w="1300px">
        <Alert
          variant="light"
          color="yellow"
          title="검사 계획 없음"
          icon={<IconCircleDashed size={16} />}
        >
          진행 중인 검사 계획이 없습니다. 계획을 생성한 뒤 다시 시도해주세요.
        </Alert>
      </Stack>
    );
  }

  if (inspectionUnits.length === 0) {
    return (
      <Stack spacing="lg" w="1300px">
        <Alert
          variant="light"
          color="blue"
          title="샘플 정보 없음"
          icon={<IconCircleDashed size={16} />}
        >
          검사할 샘플 정보가 없습니다. 샘플 목록을 확인해주세요.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing="lg" w="1300px">
      <Paper p="md" withBorder>
        <Flex justify="space-between" align="flex-start">
          <Stack spacing="xs">
            <Flex gap="xs">
              {onCancel && (
                <Button
                  variant="subtle"
                  size="compact-sm"
                  leftIcon={<IconArrowLeft size={16} />}
                  onClick={onCancel}
                >
                  이전 단계
                </Button>
              )}
              <Badge color="blue" variant="light">
                {currentPlan.inspectionType}
              </Badge>
              <Badge
                color={currentPlan.status === "SUBMITTED" ? "green" : "grape"}
                variant="light"
              >
                {currentPlan.status === "SUBMITTED"
                  ? "검사 완료"
                  : "검사 진행중"}
              </Badge>
            </Flex>
            <Title order={3}>검사 실행</Title>
            <Flex gap="md">
              <Text size="sm" c="dimmed">
                문서: {currentPlan.documentNo}
              </Text>
              <Text size="sm" c="dimmed">
                품목: {currentPlan.item}
              </Text>
              <Text size="sm" c="dimmed">
                Lot: {currentPlan.lot}
              </Text>
              <Text size="sm" c="dimmed">
                수량: {currentPlan.quantity} EA
              </Text>
            </Flex>
          </Stack>
        </Flex>
      </Paper>

      <SimpleGrid cols={2}>
        <Card withBorder>
          <Stack spacing="xs">
            <Text size="md" c="dimmed">
              진행률
            </Text>
            <Flex justify="space-between" align="center">
              <Text size="xl" fw={700}>
                {completedCount} / {inspectionUnits.length}
              </Text>
              <Text size="md" c="dimmed">
                {progressValue}%
              </Text>
            </Flex>
            <Progress value={progressValue} />
          </Stack>
        </Card>
        <Card withBorder>
          <Stack spacing="xs">
            <Text size="md" c="dimmed">
              샘플 정보
            </Text>
            <Flex gap="lg">
              <div>
                <Text size="sm" c="dimmed">
                  총 샘플
                </Text>
                <Text fw={600}>{inspectionUnits.length} 개</Text>
              </div>
              <div>
                <Text size="sm" c="dimmed">
                  현재 선택
                </Text>
                <Text fw={600}>{currentUnit.label}</Text>
              </div>
            </Flex>
          </Stack>
        </Card>
      </SimpleGrid>

      <Card withBorder>
        <Stack spacing="md">
          {inspectionCriteria.length === 0 ? (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="gray"
              variant="light"
            >
              선택된 검사 기준이 없어 표를 구성할 수 없습니다.
            </Alert>
          ) : (
            <ScrollArea style={{ width: 1100 }}>
              <Table withColumnBorders highlightOnHover striped>
                <thead>
                  <tr>
                    <th style={{ width: 160 }}>샘플</th>
                    <th style={{ width: 120 }}>상태</th>
                    {inspectionCriteria.map((criteria) => {
                      const meta = criteriaTypeMeta[criteria.type];
                      return (
                        <th key={criteria.id} style={{ minWidth: 180 }}>
                          <Stack spacing={5}>
                            <Flex justify="space-between">
                              <Text fw={600}>{criteria.name}</Text>
                              {criteria.required && (
                                <Badge size="xs" color="pink" variant="light">
                                  필수
                                </Badge>
                              )}
                            </Flex>
                            <Text size="sm" c="dimmed">
                              {getCriteriaSpec(criteria)}
                            </Text>
                            <Badge
                              size="xs"
                              color={meta?.color ?? "gray"}
                              variant="outline"
                            >
                              {meta?.label ?? criteria.type}
                            </Badge>
                            {criteria.type === "boolean" && (
                              <Flex gap={4} align="center">
                                <Flex
                                  justify={"space-between"}
                                  w={"100%"}
                                  align={"center"}
                                >
                                  <Checkbox
                                    size="xs"
                                    label="전체"
                                    disabled={inspectionUnits.length === 0}
                                    checked={
                                      checkColumnStates[criteria.id]?.checked ??
                                      false
                                    }
                                    indeterminate={
                                      checkColumnStates[criteria.id]
                                        ?.indeterminate ?? false
                                    }
                                    onChange={(event) =>
                                      handleCheckColumnToggle(
                                        criteria.id,
                                        event.currentTarget.checked,
                                      )
                                    }
                                  />
                                  <Button
                                    variant="subtle"
                                    size="compact-xs"
                                    onClick={() =>
                                      handleCheckColumnToggle(criteria.id, null)
                                    }
                                    disabled={inspectionUnits.length === 0}
                                  >
                                    초기화
                                  </Button>
                                </Flex>
                              </Flex>
                            )}
                          </Stack>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {inspectionUnits.map((unit, rowIndex) => {
                    const originalLabel = unit.originalLabel ?? unit.label;
                    const isLabelModified = originalLabel !== unit.label;
                    const rightSection =
                      renamingUnitId === unit.id ? (
                        <Loader size={12} color="gray" />
                      ) : isLabelModified ? (
                        <Badge size="xs" color="yellow" variant="light">
                          수정됨
                        </Badge>
                      ) : undefined;
                    return (
                      <tr
                        key={unit.id}
                        style={{
                          backgroundColor:
                            rowIndex === currentUnitIndex
                              ? "#f8fafc"
                              : undefined,
                          cursor: "pointer",
                        }}
                        onClick={() => handleUnitSelect(rowIndex)}
                      >
                        <td>
                          <TextInput
                            size="xs"
                            value={unitLabelDrafts[unit.id] ?? unit.label}
                            onChange={(event) =>
                              handleSampleLabelInputChange(
                                unit.id,
                                event.currentTarget.value,
                              )
                            }
                            onBlur={() => {
                              void handleSampleLabelSubmit(unit, rowIndex);
                            }}
                            onKeyDown={(event) =>
                              handleSampleInputKeyDown(event, unit)
                            }
                            disabled={renamingUnitId === unit.id}
                            rightSection={rightSection}
                            rightSectionWidth={isLabelModified ? 64 : undefined}
                            placeholder="샘플명"
                            styles={{
                              input: {
                                backgroundColor: isLabelModified
                                  ? "#fff7ed"
                                  : undefined,
                                borderColor: isLabelModified
                                  ? "#f97316"
                                  : undefined,
                                color: isLabelModified ? "#c2410c" : undefined,
                                fontWeight: isLabelModified ? 600 : undefined,
                              },
                            }}
                          />
                        </td>
                        <td>
                          <Badge
                            color={statusMeta[unit.status].color}
                            variant="light"
                          >
                            {statusMeta[unit.status].label}
                          </Badge>
                        </td>
                        {inspectionCriteria.map((criteria) => (
                          <td key={`${unit.id}-${criteria.id}`}>
                            {renderCriteriaCell(
                              criteria,
                              unit,
                              handleCellChange,
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </ScrollArea>
          )}
        </Stack>
      </Card>

      {showActionBar && (
        <Paper
          p="md"
          withBorder
          style={{
            position: "sticky",
            bottom: 0,
            background: "white",
            zIndex: 20,
          }}
        >
          <Flex justify="space-between" align="center">
            <Flex gap="sm">
              {autoSaveStatus === "saving" && (
                <>
                  <IconClock size={18} color="#0ea5e9" />
                  <Text size="sm" c="cyan">
                    자동 저장 중...
                  </Text>
                </>
              )}
              {autoSaveStatus === "saved" && lastSavedInfo && (
                <>
                  <IconDeviceFloppy size={18} color="#22c55e" />
                  <Text size="sm" c="green">
                    {lastSavedInfo.unitLabel} 저장됨 (
                    {new Date(lastSavedInfo.timestamp).toLocaleTimeString(
                      "ko-KR",
                    )}
                    )
                  </Text>
                </>
              )}
              {autoSaveStatus === "idle" && (
                <>
                  <IconDeviceFloppy size={18} color="#94a3b8" />
                  <Text size="sm" c="dimmed">
                    입력 시 자동 저장됩니다.
                  </Text>
                </>
              )}
            </Flex>
            <Flex gap="sm">
              {onCancel && (
                <Button
                  variant="outline"
                  leftIcon={<IconFlag3 size={16} />}
                  onClick={onCancel}
                >
                  계획 수정
                </Button>
              )}
              <Button
                color="indigo"
                leftIcon={<IconCheck size={16} />}
                onClick={handleCompleteInspection}
                disabled={!readyToFinish}
              >
                검사 완료
              </Button>
            </Flex>
          </Flex>
        </Paper>
      )}
    </Stack>
  );
}

function renderCriteriaCell(
  criteria: InspectionCriteria,
  unit: InspectionUnit,
  onChange: (unitId: string, criteriaId: string, value: unknown) => void,
) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const value = unit.data?.[criteria.id];
  switch (criteria.type) {
    case "boolean": {
      const checkboxColor =
        value === true ? "green" : value === false ? "red" : "gray";
      return (
        <Checkbox
          size="xs"
          checked={value === true}
          indeterminate={value === null || value === undefined}
          color={checkboxColor}
          title={
            value === true ? "합격" : value === false ? "불합격" : "미진행"
          }
          onChange={() => {
            const nextValue =
              value === true ? false : value === false ? null : true;
            onChange(unit.id, criteria.id, nextValue);
          }}
        />
      );
    }
    case "number":
      return (
        <NumberInput
          size="xs"
          hideControls
          value={typeof value === "number" ? value : undefined}
          onChange={(val) => {
            if (val === "") {
              onChange(unit.id, criteria.id, null);
            } else {
              onChange(unit.id, criteria.id, Number(val));
            }
          }}
          placeholder="입력"
          // value 값이 min 과 max 사이에 있지 않으면 배경색을 경고표시
          error={
            (typeof value === "number" &&
              criteria.min !== undefined &&
              value < criteria.min) ||
            (typeof value === "number" &&
              criteria.max !== undefined &&
              value > criteria.max) ? (
              <Text size="xs">{getCriteriaSpec(criteria)}</Text>
            ) : undefined
          }
        />
      );
    case "select":
      return (
        <Select
          size="xs"
          data={(criteria.options ?? []).map((option) => ({
            label: option,
            value: option,
          }))}
          value={typeof value === "string" ? value : null}
          onChange={(val) => onChange(unit.id, criteria.id, val)}
          placeholder="선택"
        />
      );
    case "description": {
      const descriptionValue = getDescriptionCellState(value);
      return (
        <Flex gap="xs" align="center" wrap="nowrap">
          <TextInput
            size="xs"
            value={descriptionValue.text}
            onChange={(event) =>
              onChange(unit.id, criteria.id, {
                text: event.currentTarget.value,
                isPassed: descriptionValue.isPassed,
              })
            }
            placeholder="내용"
            style={{ flex: 1 }}
          />
          <Checkbox
            size="xs"
            label="합격"
            checked={descriptionValue.isPassed}
            onChange={(event) =>
              onChange(unit.id, criteria.id, {
                text: descriptionValue.text,
                isPassed: event.currentTarget.checked,
              })
            }
          />
        </Flex>
      );
    }
    case "photo": {
      const files = Array.isArray(value)
        ? (value as File[])
        : value
          ? ([value] as File[])
          : [];
      return (
        <FileInput
          size="xs"
          multiple
          value={files.length > 0 ? files : undefined}
          onChange={(nextFiles) =>
            onChange(unit.id, criteria.id, nextFiles ?? [])
          }
          accept="image/*"
          clearable
        />
      );
    }
    default:
      return (
        <TextInput
          size="xs"
          value={typeof value === "string" ? value : ""}
          onChange={(event) =>
            onChange(unit.id, criteria.id, event.currentTarget.value)
          }
          placeholder="내용"
        />
      );
  }
}
